<template>
  <div class="delivery-fee-rules mt-6">
    <!-- Promotions Display -->
    <PromotionsTable 
      v-if="props.viewMode === 'list'"
      :promotions="promotions" 
      :loading="loading" 
      @edit="editPromotion" 
      @toggle="togglePromotion"
      @delete="deletePromotion" 
    />
    <PromotionsCards 
      v-else
      :promotions="promotions" 
      :loading="loading" 
      @edit="editPromotion" 
      @toggle="togglePromotion"
      @delete="deletePromotion" 
    />
  </div>

  <!-- Modal de Creación/Edición -->
  <PromotionModal
    v-if="props.showCreateModal || editingPromotion"
    :promotion="editingPromotion"
    :menu-items="menuItems"
    @save="handleSavePromotion"
    @close="closeModal"
  />

  <!-- Modal de Confirmación de Eliminación -->
  <DeleteConfirmationModal
    v-if="showDeleteModal"
    :item="deletingPromotion"
    item-type="promoción"
    @confirm="confirmDelete"
    @cancel="showDeleteModal = false"
  />
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';

// Props
const props = defineProps<{
  showCreateModal?: boolean;
  viewMode: 'list' | 'card';
}>();

// Emits
const emit = defineEmits<{
  'close-modal': [];
}>();
import { useToast } from 'vue-toastification';
import promotionService from '@/services/promotionService';
import menuService from '@/services/menuService';
import type { Promotion } from '@/types/promotion';
import PromotionModal from './PromotionModal.vue';
import DeleteConfirmationModal from './DeleteConfirmationModal.vue';
import PromotionsTable from './PromotionsTable.vue';
import PromotionsCards from './PromotionsCards.vue';

const toast = useToast();

const promotions = ref<Promotion[]>([]);
const menuItems = ref<any[]>([]);
const editingPromotion = ref<Promotion | null>(null);
const showDeleteModal = ref(false);
const deletingPromotion = ref<Promotion | null>(null);
const loading = ref(false);
const savingPromotion = ref(false);
const deletingInProgress = ref(false);
const togglingPromotionId = ref<number | null>(null);

// Cargar datos
onMounted(async () => {
  await Promise.all([loadPromotions(), loadMenuItems()]);
});

const replacePromotionInList = (updated: Promotion) => {
  if (updated.id == null) {
    return;
  }

  const index = promotions.value.findIndex(p => p.id === updated.id);
  if (index !== -1) {
    promotions.value[index] = updated;
  }
};

const resetDeleteState = () => {
  showDeleteModal.value = false;
  deletingPromotion.value = null;
};

const loadPromotions = async () => {
  try {
    loading.value = true;
    promotions.value = await promotionService.getAllPromotions();
  } catch (error: any) {
    toast.error('Error al cargar promociones');
    console.error('Error loading promotions:', error);
  } finally {
    loading.value = false;
  }
};

const loadMenuItems = async () => {
  try {
    menuItems.value = await menuService.getMenuItems();
  } catch (error: any) {
    console.error('Error loading menu items:', error);
  }
};

const editPromotion = async (promotion: Promotion) => {
  if (!promotion.id) {
    toast.error('Promoción inválida: no tiene ID');
    return;
  }

  try {
    const promotionData = await promotionService.getPromotionById(promotion.id);
    editingPromotion.value = promotionData;
  } catch (error: any) {
    toast.error('Error al cargar los datos de la promoción');
    console.error('Error fetching promotion by ID:', error);
  }
};

const deletePromotion = (promotion: Promotion) => {
  deletingPromotion.value = promotion;
  showDeleteModal.value = true;
};

const togglePromotion = async (promotion: Promotion) => {
  if (promotion.id == null) {
    toast.error('Promoción inválida');
    return;
  }

  if (togglingPromotionId.value === promotion.id) {
    return;
  }

  try {
    togglingPromotionId.value = promotion.id;
    const updated = await promotionService.togglePromotion(promotion.id, !promotion.is_active);
    replacePromotionInList(updated);
    toast.success(`Promoción ${updated.is_active ? 'activada' : 'desactivada'} correctamente`);
  } catch (error: any) {
    toast.error('Error al cambiar estado de la promoción');
    console.error('Error toggling promotion:', error);
  } finally {
    togglingPromotionId.value = null;
  }
};

const confirmDelete = async () => {
  if (!deletingPromotion.value || deletingPromotion.value.id == null || deletingInProgress.value) {
    return;
  }

  try {
    deletingInProgress.value = true;
    const promotionId = deletingPromotion.value.id;
    await promotionService.deletePromotion(promotionId);
    promotions.value = promotions.value.filter(p => p.id !== promotionId);
    toast.success('Promoción eliminada correctamente');
  } catch (error: any) {
    toast.error('No se puede eliminar la promoción - error en el backend. Contacte al administrador.');
    console.error('Error deleting promotion:', error);
  } finally {
    deletingInProgress.value = false;
    resetDeleteState();
  }
};

const handleSavePromotion = async (promotionData: Omit<Promotion, 'id' | 'created_at' | 'updated_at' | 'used_count'>) => {
  if (savingPromotion.value) {
    return;
  }

  const currentEditingId = editingPromotion.value?.id;

  try {
    savingPromotion.value = true;

    if (currentEditingId != null) {
      const updated = await promotionService.updatePromotion(currentEditingId, promotionData);
      replacePromotionInList(updated);
      toast.success('Promoción actualizada correctamente');
    } else {
      const created = await promotionService.createPromotion(promotionData);
      promotions.value.unshift(created);
      toast.success('Promoción creada correctamente');
    }

    closeModal();
  } catch (error: any) {
    toast.error(currentEditingId != null ? 'Error al actualizar promoción' : 'Error al crear promoción');
    console.error('Error saving promotion:', error);
  } finally {
    savingPromotion.value = false;
  }
};

const closeModal = () => {
  if (props.showCreateModal) {
    emit('close-modal');
  }
  editingPromotion.value = null;
};

</script>
